import { Area, Folder } from '../Infrastructure'
import { traverse } from '../Infrastructure/services'
import {
    AssetCondition,
    GroupedInventoryAssets,
    InventoryAsset,
    InventoryAssetStatus,
    ProductDocumentType,
    UnitInventoryResponse,
} from './types'

export interface AreaLabelContext {
    areaById?: Record<number, Area>
    areaConfigNames?: Record<number, string>
}

const STATUS_COLORS: Record<InventoryAssetStatus, string> = {
    active: '#4CAF50',
    pending_replacement: '#FF9800',
    removed: '#F44336',
    replaced: '#2196F3',
    maintenance: '#FFC107',
}

const CONDITION_COLORS: Record<AssetCondition, string> = {
    new: '#4CAF50',
    good: '#007AFF',
    fair: '#FF9800',
    poor: '#F44336',
}

export const getStatusColor = (status: InventoryAssetStatus): string =>
    STATUS_COLORS[status] ?? '#9e9e9e'

export const getConditionColor = (condition: AssetCondition): string =>
    CONDITION_COLORS[condition] ?? '#9e9e9e'

export const formatStatusLabel = (status: InventoryAssetStatus): string =>
    status
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

export const formatConditionLabel = (condition: AssetCondition): string =>
    condition.charAt(0).toUpperCase() + condition.slice(1)

export const formatDocumentTypeLabel = (
    documentType: ProductDocumentType,
): string => {
    switch (documentType) {
        case 'manual':
            return 'Manual'
        case 'warranty':
            return 'Warranty'
        case 'spec_sheet':
            return 'Spec sheet'
        case 'other':
            return 'Other'
    }
}

export const getProductProfileLabel = (profile: {
    brand: string
    model_number: string
    display_name?: string
}): string =>
    profile.display_name?.trim() ||
    `${profile.brand} ${profile.model_number}`.trim()

export const getPhotoUrl = (photo: {
    frame_image?: string
    image_url?: string
}): string => photo.image_url || photo.frame_image || ''

export const getPrimaryPhoto = (asset: InventoryAsset): string | undefined => {
    const photos = asset.photos ?? []
    const primary = photos.find((photo) => photo.is_primary)
    if (primary) {
        return getPhotoUrl(primary)
    }
    if (photos.length > 0) {
        return getPhotoUrl(photos[0])
    }
    return undefined
}

export const getInventoryAreaDisplayLabel = (
    asset: InventoryAsset,
    context?: AreaLabelContext,
): string => {
    if (asset.area && context?.areaById?.[asset.area]) {
        const area = context.areaById[asset.area]
        const configName =
            area.area_config_name ??
            context.areaConfigNames?.[area.area_config] ??
            asset.area_name ??
            ''
        const areaLabel = area.area_label ?? ''

        if (configName && areaLabel) {
            const combined = `${configName} ${areaLabel}`.trim()
            if (
                areaLabel.toLowerCase().includes(configName.toLowerCase()) ||
                combined.toLowerCase() === areaLabel.toLowerCase()
            ) {
                return areaLabel
            }
            return combined
        }
        if (configName) {
            return configName
        }
        if (areaLabel) {
            return areaLabel
        }
    }

    const areaName = asset.area_name?.trim() ?? ''
    const roomLabel = asset.room_label?.trim() ?? ''

    if (!roomLabel) {
        return areaName || 'Unassigned'
    }

    if (/^\d+$/.test(roomLabel)) {
        return areaName ? `${areaName} ${roomLabel}` : roomLabel
    }

    const suffixFirstMatch = roomLabel.match(/^(\d+)\s+(.+)$/)
    if (suffixFirstMatch) {
        const normalized = `${suffixFirstMatch[2]} ${suffixFirstMatch[1]}`
        if (
            areaName &&
            !normalized.toLowerCase().includes(areaName.toLowerCase())
        ) {
            return normalized
        }
        return normalized
    }

    if (areaName && roomLabel.toLowerCase().includes(areaName.toLowerCase())) {
        return roomLabel
    }

    return areaName ? `${areaName} ${roomLabel}` : roomLabel
}

export const flattenUnitInventory = (
    response: UnitInventoryResponse,
): InventoryAsset[] => {
    return Object.values(response.areas).flat()
}

export const normalizeInventoryAreas = (
    assets: InventoryAsset[],
    context?: AreaLabelContext,
): GroupedInventoryAssets[] => {
    const groups = new Map<string, InventoryAsset[]>()

    assets.forEach((asset) => {
        const label = getInventoryAreaDisplayLabel(asset, context)
        const existing = groups.get(label) ?? []
        existing.push(asset)
        groups.set(label, existing)
    })

    return Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .map(([displayLabel, groupAssets]) => ({
            displayLabel,
            assets: groupAssets.sort((left, right) =>
                left.name.localeCompare(right.name),
            ),
        }))
}

export const buildAreaLabelContextFromUnit = (
    unitAreas: Area[],
    areaConfigNames: Record<number, string>,
): AreaLabelContext => ({
    areaById: unitAreas.reduce<Record<number, Area>>((map, area) => {
        map[area.id] = area
        return map
    }, {}),
    areaConfigNames,
})

export interface PropertyOption {
    id: number
    name: string
    apartmentId: number | null
}

export const collectPropertyOptions = (root: Folder): PropertyOption[] => {
    const properties: PropertyOption[] = []
    const seenApartmentIds = new Set<number>()

    traverse(root, (folder) => {
        if (folder.apartmentOwner) {
            if (!seenApartmentIds.has(folder.apartmentOwner.id)) {
                seenApartmentIds.add(folder.apartmentOwner.id)
                properties.push({
                    id: folder.id,
                    name: folder.apartmentOwner.name || folder.name,
                    apartmentId: folder.apartmentOwner.id,
                })
            }
        }
    })

    if (properties.length === 0) {
        root.children.forEach((child) => {
            properties.push({
                id: child.id,
                name: child.name,
                apartmentId: null,
            })
        })
    }

    return properties.sort((a, b) => a.name.localeCompare(b.name))
}

export const collectUnitsInFolder = (
    root: Folder,
    propertyFolderId: number,
): { id: number; name: string }[] => {
    let targetFolder: Folder | null = null

    traverse(root, (folder) => {
        if (folder.id === propertyFolderId) {
            targetFolder = folder
        }
    })

    if (!targetFolder) {
        return []
    }

    const units: { id: number; name: string }[] = []

    const walk = (folder: Folder) => {
        folder.units.forEach((unit) => {
            units.push({ id: unit.id, name: unit.name })
        })
        folder.children.forEach(walk)
    }

    walk(targetFolder)

    return units.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
}

export const findUnitById = (root: Folder, unitId: number) => {
    let found: { id: number; name: string; areas: Area[] } | undefined

    traverse(root, (folder) => {
        folder.units.forEach((unit) => {
            if (unit.id === unitId) {
                found = {
                    id: unit.id,
                    name: unit.name,
                    areas: unit.areas,
                }
            }
        })
    })

    return found
}

export interface InventoryFilters {
    search: string
    area: string | null
    condition: AssetCondition | null
    status: InventoryAssetStatus | null
}

export const assetMatchesSearch = (
    asset: InventoryAsset,
    search: string,
    context?: AreaLabelContext,
): boolean => {
    const query = search.trim().toLowerCase()
    if (!query) {
        return true
    }

    const displayLabel = getInventoryAreaDisplayLabel(asset, context)
    const haystack = [
        asset.name,
        asset.category,
        asset.brand,
        asset.model_number,
        asset.serial_number,
        asset.area_name,
        asset.room_label,
        displayLabel,
        asset.specs,
        asset.condition_notes,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

    return haystack.includes(query)
}

export const filterInventoryAssets = (
    assets: InventoryAsset[],
    filters: InventoryFilters,
    context?: AreaLabelContext,
): InventoryAsset[] =>
    assets.filter((asset) => {
        if (!assetMatchesSearch(asset, filters.search, context)) {
            return false
        }

        if (filters.area) {
            const label = getInventoryAreaDisplayLabel(asset, context)
            if (label !== filters.area) {
                return false
            }
        }

        if (filters.condition && asset.condition !== filters.condition) {
            return false
        }

        if (filters.status && asset.status !== filters.status) {
            return false
        }

        return true
    })
